import React, { useEffect } from 'react';
import { useSafeState } from '@strava/react-hooks/src/use-safe-state';
import AvatarFallback from '@strava/icons/NavigationProfileHighlightedMedium';
import EmployeeBadge from '@strava/icons/BadgesMulticolorSuperuserSmall';
import SubscriberBadge from '@strava/icons/BadgesMulticolorSummitSmall';
import ProBadge from '@strava/icons/BadgesMulticolorProSmall';
import VerifiedBadge from '@strava/icons/BadgesMulticolorVerifiedSmall';
import clsx from '../clsx';

import styles from './Avatar.module.scss';

interface ImgProps {
  src: string;
  alt: string;
  title: string;
  fallback?: React.ReactNode;
}

const Img = ({ src: srcProp, alt, title, fallback = null }: ImgProps) => {
  const [error, setError] = useSafeState(false);
  const [src, setSrc] = useSafeState('');

  const handleError = () => {
    setError(true);
  };

  useEffect(() => {
    if (srcProp) {
      setError(false);
      setSrc(srcProp);
    }
  }, [srcProp]);

  return error ? (
    <>{fallback}</>
  ) : (
    <img src={src} alt={alt} title={title} onError={handleError} />
  );
};

function getBadge(badge: string) {
  switch (badge) {
    case 'ambassador':
      return <ProBadge />;
    case 'employee':
      return <EmployeeBadge />;
    case 'subscriber':
    case 'premium':
      return <SubscriberBadge />;
    case 'pro':
      return <ProBadge />;
    case 'verified':
      return <VerifiedBadge />;
    default:
      return null;
  }
}

export type AvatarType = 'athlete' | 'club';
export type AvatarSize = 'xsmall' | 'small' | 'default' | 'large' | 'xlarge';
export type AvatarBadge =
  | '' // in some cases active has member_type as empty string for default users
  | 'ambassador'
  | 'employee'
  | 'subscriber'
  | 'premium' // legacy name referring to 'subscriber', used in active
  | 'pro'
  | 'verified';

interface BaseAvatarProps {
  src: string;
  type: AvatarType;
  name?: string;
  alt?: string;
  title?: string;
  size?: AvatarSize;
  badge?: AvatarBadge;
  className?: string;
}

type AnchorProps = React.ComponentPropsWithoutRef<'a'> & {
  href?: string;
};
type DivProps = React.ComponentPropsWithoutRef<'div'> & {
  href?: never;
};

// Guard to check if href exists in props
const hasHref = (props: AnchorProps | DivProps): props is AnchorProps =>
  'href' in props;

type AvatarProps = BaseAvatarProps & (AnchorProps | DivProps);

/**
 * Prop Deprecation Warning: We are slowly deprecating the `name` prop in favor of the `title` prop
 * so that the React props match up to the element's attribute.
 * */
const Avatar = ({
  name = '',
  alt = '',
  title,
  type,
  src,
  size,
  badge,
  className,
  ...otherProps
}: AvatarProps) => {
  const classNames = clsx([
    styles.avatar,
    styles[type],
    className,
    size && styles[size]
  ]);

  // TODO: uncomment when existing Avatar usage is refactored. Otherwise, there will be a lot of warning in the console.
  // if (name || process.env.NODE_ENV !== 'production') {
  //   console.warn(
  //     '[Prop Deprecation Warning] Deprecating the `name` prop in favor of the `title`.'
  //   );
  // }

  const children = (
    <>
      {badge && <div className={styles.badge}>{getBadge(badge)}</div>}
      <div className={styles.imgWrapper}>
        <Img
          src={src}
          alt={alt}
          title={title || name}
          fallback={type === 'athlete' ? <AvatarFallback /> : null}
        />
      </div>
    </>
  );

  if (hasHref(otherProps)) {
    return (
      <a className={classNames} data-testid="avatar-wrapper" {...otherProps}>
        {children}
      </a>
    );
  }

  return (
    <div
      className={classNames}
      data-testid="avatar-wrapper"
      {...(otherProps as DivProps)}
    >
      {children}
    </div>
  );
};

export default Avatar;
